import { ClientBusinessPartialModel } from 'app/client/clientBusiness/models/ClientBusiness';
import { Field, Form, Formik } from 'formik';
import { CalendarDays, Check, X } from 'lucide-react';
import moment from 'moment';
import { useState } from 'react';
import Button from 'shared/components/Button';
import FormField from 'shared/components/form/FormField';
import FormikInput from 'shared/components/form/FormikInput';
import StyledAutocomplete from 'shared/components/StyledAutocomplete';
import StyledDatePicker from 'shared/components/StyledDatePicker';
import * as Yup from 'yup';
import { EventModel } from '../../models/Evento';
import { UpdateEventPayload } from '../../services/useEventoService';
import { PencilButton } from 'shared/components/IconButtons';
import { useThemeContext } from 'shared/context/themeContext';
// import { dark as darkColors} from 'shared/theme/colorTokens';

interface FormValues {
  name: string;
  description: string;
  businessClientId: number | null;
  isActive: boolean;
  fromDate: string;
  toDate: string;
}

interface Props {
  event: EventModel;
  clients: ClientBusinessPartialModel[];
  isSaving: boolean;
  onSave: (payload: UpdateEventPayload) => void;
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('El nombre es requerido')
    .min(3, 'El nombre no puede tener menos de 3 caracteres'),
  businessClientId: Yup.number().nullable().required('El cliente es requerido'),
  fromDate: Yup.string().required('La fecha "desde" es requerida'),
  toDate: Yup.string()
    .required('La fecha "hasta" es requerida')
    .test(
      'after-from',
      'La fecha "hasta" no puede ser anterior a "desde"',
      function (value) {
        const { fromDate } = this.parent;
        if (!value || !fromDate) {
          return true;
        }
        return value >= fromDate;
      }
    )
});

// Label de solo lectura (modo vista de la card); los labels de formulario
// vienen de `FormField`.
const labelClass =
  'text-xs font-medium uppercase tracking-wide text-inkMuted dark:text-inkMuted-dark';

const formatDates = (fromDate: string | null, toDate: string | null) => {
  if (!fromDate) {
    return '—';
  }
  const from = moment(fromDate).format('DD MMM YYYY');
  if (!toDate || toDate === fromDate) {
    return from;
  }
  return `${from} - ${moment(toDate).format('DD MMM YYYY')}`;
};

const EventoGeneralDataCard = ({ event, clients, isSaving, onSave }: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const { theme } = useThemeContext();
  const isDarkTheme = theme === 'dark';

  const initialValues: FormValues = {
    name: event.name,
    description: event.description ?? '',
    businessClientId: event.businessClient?.id ?? null,
    isActive: event.isActive,
    fromDate: event.fromDate ?? '',
    toDate: event.toDate ?? ''
  };

  return (
    <div
      className="rounded-2xl border border-secondary-200 bg-general-card-light p-6 border-white/20 dark:bg-general-card-dark"
      style={{}}
    >
      {isEditing ? (
        <Formik<FormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            onSave({
              name: values.name,
              description: values.description || undefined,
              business_client_id: values.businessClientId as number,
              is_active: values.isActive,
              from_date: values.fromDate,
              to_date: values.toDate
            });
            setIsEditing(false);
          }}
        >
          {({ values, errors, touched, setFieldValue, dirty }) => (
            <Form className="flex flex-col gap-4">
              <h2 className="text-lg font-semibold text-ink dark:text-ink-dark">
                Editar evento
              </h2>
              <FormField
                label="Cliente"
                error={touched.businessClientId && errors.businessClientId}
              >
                <StyledAutocomplete<ClientBusinessPartialModel>
                  options={clients}
                  value={
                    clients.find(
                      (client) => client.id === values.businessClientId
                    ) ?? null
                  }
                  getOptionLabel={(client) => client?.fantasyName ?? ''}
                  onChange={(value) =>
                    setFieldValue('businessClientId', value ? value.id : null)
                  }
                  placeholder="Seleccionar cliente"
                />
              </FormField>
              <FormikInput name="name" label="Nombre" />
              <FormikInput
                name="description"
                label="Descripción"
                multiline
                rows={2}
              />
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  label="Desde"
                  error={touched.fromDate && errors.fromDate}
                >
                  <StyledDatePicker
                    value={values.fromDate || null}
                    onChange={(value) => setFieldValue('fromDate', value ?? '')}
                  />
                </FormField>
                <FormField
                  label="Hasta"
                  error={touched.toDate && errors.toDate}
                >
                  <StyledDatePicker
                    value={values.toDate || null}
                    onChange={(value) => setFieldValue('toDate', value ?? '')}
                    minDate={values.fromDate || null}
                  />
                </FormField>
              </div>
              <label className="flex items-center gap-3">
                <Field
                  type="checkbox"
                  name="isActive"
                  className="h-4 w-4 accent-secondary-500"
                />
                <span className="text-sm text-ink dark:text-ink-dark">
                  Evento activo
                </span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsEditing(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" size="sm" disabled={isSaving || !dirty}>
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-ink dark:text-ink-dark">
              Datos generales
            </h2>
            <PencilButton onClick={() => setIsEditing(true)} />
          </div>

          <div>
            <p className={labelClass}>Cliente</p>
            <p className="text-lg font-medium text-secondary-800 dark:text-ink-dark">
              {event.businessClient?.name ?? '—'}
            </p>
          </div>

          <div>
            <p className={labelClass}>Nombre del evento</p>
            <p className="text-lg font-medium text-ink dark:text-ink-dark">
              {event.name}
            </p>
          </div>

          {event.description && (
            <p className="text-sm text-inkMuted dark:text-inkMuted-dark">
              {event.description}
            </p>
          )}

          <div className="flex items-center gap-2">
            <CalendarDays size={16} className="text-secondary-500" />
            <p className="text-sm text-ink dark:text-ink-dark">
              {formatDates(event.fromDate, event.toDate)}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {event.isActive ? (
              <Check size={16} className="text-emerald-500" />
            ) : (
              <X size={16} className="text-inkMuted-dark" />
            )}
            <span className="text-sm text-ink dark:text-ink-dark">
              {event.isActive ? 'Activo' : 'Inactivo'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventoGeneralDataCard;
